import React from "react";
import {
	SEGMENT_BZA,
	URL_BZA_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
	URL_BZA_AIRCRAFT_DATA,
	URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
	URL_CMC_AIRFRAME_MRO_EVENT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productSegmentPageProps } from "../../common/types/product";

export default function BusinessJetAircraftHistoryData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Search for full historical business jet operators' data down to an aircraft level and get access to full business jet history for all inactive aircraft from 2000 up to the present day."
				packages={[
					{
						label: SEGMENT_BZA,
						features: [
							"Search historical operator data for 29,000 individual business jets",
							"Track business jet operator changes and fleet growth over time",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Business Jet Aircraft History",
						description:
							"Get insights into business jets history on an aircraft level including:",
						list: [
							"An operator’s fleet development overview by market group",
							"An operator’s fleet delivery by year (new, used, and overall aircraft deliveries)",
							"Full aircraft operator history for all inactive aircraft from 2000 up to the present day",
							"An operator history overview on a tail level",
							"Annual deliveries of a specific aircraft variant",
							"Annual active aircraft by variant",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-history-data/aircraft-profile-overview.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for business jets worldwide using a range of filters, such as location, operator, aircraft type, engine type, dates and more."
				slides={[
					{
						title: "Location",
						description:
							"Search for business jet aircraft history based on the operator's location:",
						list: [
							"Operator continent",
							"Operator country",
							"Operator region",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-history-data/aircraft-history-search-by-location.jpg",
							title: "Overview of the business jet operators’ fleet located in Spain in 2015",
						},
					},
					{
						title: "Operator",
						description:
							"Search for a specific business jet operator’s history by:",
						list: [
							"Including or excluding multiple operators",
							"Operator name",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-history-data/aircraft-history-search-by-operator.jpg",
							title: "Flexjet historic fleet overview until 2025",
						},
					},
					{
						title: "Aircraft Type",
						description:
							"Search for business jet aircraft history by:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft production line",
							"Aircraft family/type/variant",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-history-data/aircraft-history-search-by-aircraft-type.jpg",
							title: "Historical development of midsize jets",
						},
					},
					{
						title: "Aircraft",
						description:
							"Search for business jet aircraft history by:",
						list: [
							"Aircraft registration",
							"Line/serial number",
							"Aircraft hexcode",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-history-data/aircraft-history-search-by-aircraft.jpg",
							title: "Operator history of the aircraft registration N600AN",
						},
					},
					{
						title: "Engines",
						description:
							"Search for business jet aircraft history based on the engines installed:",
						list: [
							"Engine manufacturer",
							"Engine type",
							"Engine subtype",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-history-data/aircraft-history-search-by-engines.jpg",
							title: "Overview of all business jets powered by Rolls-Royce, in operation in 2024",
						},
					},
					{
						title: "Dates",
						description:
							"Search for business jet aircraft history overview based on a specific time range:",
						list: [
							"As of date – specific search period",
							"Date in - date the aircraft was delivered to the operator (regardless of other changes such as registration, variant, etc.)",
							"Date out – date the aircraft left the operator (regardless of other changes such as registration, variant, etc.)",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-history-data/aircraft-history-search-by-dates.jpg",
							title: "Overview of all business jets that entered service with operators in 2023 (based on “Date In”) ",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into business jet aircraft owners, managers, and SPVs.",
						url: URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
					},
					{
						title: "Aircraft Data",
						content:
							"Get access to more than 29,000 business jets worldwide, with detailed insights at the tail level.",
						url: URL_BZA_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft Accidents and Incidents Data",
						content:
							"Get a detailed overview of a business jet aircraft accident/incident on a tail level.",
						url: URL_BZA_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
					},
					{
						title: "Airframe MRO Event Data",
						content:
							"Get insights into over 25,000 airframe maintenance events worldwide.",
						url: URL_CMC_AIRFRAME_MRO_EVENT_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Search historical operator data for 29,000 individual business jets",
					"Track business jet ownership changes and operator fleet growth over time",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Our Business Jet Aircraft History Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"The Business Jet Aircraft History Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, Business Jet Aircraft History Data can be
								purchased as a data feed for seamless
								integration into your own system. ch-aviation
								also provides full support for the integration
								process. To learn more about data feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "How well is the historical dimension covered?",
						content: (
							<ul className="ticks ticks--small">
								<li>
									If an aircraft is with an operator – full
									historical data is presented
								</li>
								<li>
									For all inactive aircraft that operated
									during the 21st century (2000-today), we
									show the history back till the year 2000.
								</li>
								<li>
									Aircraft built and retired before 2000 – no
									historical data is presented.
								</li>
							</ul>
						),
					},
					{
						title: "How often is the data updated?",
						content:
							"The Business Jet Aircraft History Data is being updated weekly, ensuring you always have the latest information.",
					},
				]}
			/>
		</>
	);
}
